import { ChangeDetectionStrategy, Component, ElementRef, OnInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { SharedUIModule } from '@nimbus-library/features/shared/shared.ui.module';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { CompanionSplitScreenService } from '@nimbus-library/features/companion-chat/services/companion-split-screen.service';

// Sits as a flex sibling of the Companion chat panel (a component cannot render a sibling of
// itself) and is shared by the Portal and Admin shells so the resize logic is not duplicated.
// Dragging updates the `--companion-panel-width` CSS variable on the split container, clamped
// to 20-50%. Dragging below the minimum collapses the panel (same as the Collapse chat button).
// The committed width is persisted to the split-screen session so it is restored after a reload.
@Component({
  standalone: true,
  selector: 'nimbus-companion-split-screen-resizer',
  templateUrl: './companion-split-screen-resizer.component.html',
  styleUrls: ['./companion-split-screen-resizer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, SharedUIModule],
})
export class CompanionSplitScreenResizerComponent implements OnInit {
  private readonly defaultWidthPct = 40;
  private readonly minWidthPct = 20;
  private readonly maxWidthPct = 50;
  private readonly collapseFloorPct = 10;
  private readonly percentBase = 100;

  private currentWidthPct = this.defaultWidthPct;

  public readonly TranslationKeys = TranslationKeys;

  public constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly companionSplitScreenService: CompanionSplitScreenService,
  ) {}

  public ngOnInit(): void {
    const storedPct = this.companionSplitScreenService.panelWidthPct;
    if (storedPct === null) {
      return;
    }
    this.currentWidthPct = Math.max(this.minWidthPct, Math.min(this.maxWidthPct, storedPct));
    this.container?.style.setProperty('--companion-panel-width', `${this.currentWidthPct}%`);
  }

  public get panelWidthPx(): number {
    return this.pctToPx(this.currentWidthPct);
  }

  public get minWidthPx(): number {
    return this.pctToPx(this.collapseFloorPct);
  }

  public get maxWidthPx(): number {
    return this.pctToPx(this.maxWidthPct);
  }

  public onDividerChange(widthPx: number): void {
    const container = this.container;
    if (!container) {
      return;
    }
    const pct = (widthPx / container.clientWidth) * this.percentBase;
    if (pct < this.minWidthPct) {
      this.companionSplitScreenService.requestCollapse();
      return;
    }
    this.currentWidthPct = Math.min(this.maxWidthPct, pct);
    container.style.setProperty('--companion-panel-width', `${this.currentWidthPct}%`);
  }

  public onDividerCommitted(): void {
    this.companionSplitScreenService.setPanelWidthPct(this.currentWidthPct);
  }

  private get container(): HTMLElement | null {
    return this.elementRef.nativeElement.parentElement;
  }

  private pctToPx(pct: number): number {
    const container = this.container;
    return container ? (container.clientWidth * pct) / this.percentBase : 0;
  }
}
